import React, { useState, useEffect, useContext } from 'react';
import { Users, MapPin, PlusCircle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import { AuthContext } from '../../context/AuthContext';

const PostedJobs = () => {
  // Get user context to determine if user is a recruiter
  const { user } = useContext(AuthContext);
  const isRecruiter = user?.userType === 'recruiter';
  
  // Determine the correct path for posting a job based on user type
  const postJobPath = isRecruiter ? '/recruiter/post-job' : '/post-job';
  
  // Determine the correct path for viewing all jobs based on user type
  const viewAllJobsPath = isRecruiter ? '/recruiter/posted-jobs' : '/posted-jobs';
  
  // Fetch real job data from API
  const { data: apiData, loading } = useApi('jobs');
  
  // Extract jobs from nested API response structure
  // API returns: { success: true, data: { data: [...jobs], pagination: {...} } }
  const allJobs = apiData?.data?.data || apiData?.data?.jobs || apiData?.data || [];
  
  // Ensure allJobs is always an array
  const jobsArray = Array.isArray(allJobs) ? allJobs : [];
  
  // Filter jobs by current user
  const jobs = jobsArray.filter(job => {
    // Ensure job is an object
    if (!job || typeof job !== 'object') return false;
    if (!user?.id) return false;
    
    // For employers: show jobs posted by them
    if (user.userType === 'employer') {
      return job.postedBy === user.id;
    }
    
    // For recruiters: show jobs posted by them OR jobs assigned to them
    if (user.userType === 'recruiter') {
      return job.postedBy === user.id || job.recruiterId === user.id;
    }
    
    return false;
  });

  return (
    <div className="bg-white p-6 border border-black">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-black">Posted Jobs</h3>
        {jobs.length > 0 && (
          <Link to={viewAllJobsPath} className="px-4 py-1 bg-black text-white text-sm">
            View All
          </Link>
        )}
      </div>
      
      <div className="space-y-4">
        {jobs.length > 0 ? (
          jobs.map(job => (
            <div key={job.id} className="p-4 border border-black">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-medium text-black">{job.title || 'Untitled Job'}</h4>
                  <p className="text-xs text-gray-600">{job.company || job.companyName || 'Company'}</p>
                </div>
                <span className="px-2 py-1 bg-gray-100 text-xs text-black">{job.type || 'Full-time'}</span>
              </div>
              
              <div className="flex items-center text-xs text-gray-600 mb-3">
                <MapPin size={12} className="mr-1" />
                <span>{job.location || 'Remote'}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center text-xs text-gray-600">
                  <Users size={12} className="mr-1" />
                  <span>
                    {Array.isArray(job.applicants) 
                      ? job.applicants.length 
                      : (job.applicantCount || job.applications || 0)
                    } applicants
                  </span>
                </div>
                
                <div className="flex items-center text-xs text-gray-600">
                  <Clock size={12} className="mr-1" />
                  <span>{job.daysActive ? `${job.daysActive} days left` : 'Active'}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">You haven't posted any jobs yet</p>
            <Link 
              to={postJobPath} 
              className="inline-flex items-center px-6 py-3 bg-black text-white hover:bg-gray-800 transition-colors rounded-md shadow-sm"
            >
              <PlusCircle size={18} className="mr-2" />
              Post Your First Job
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostedJobs;
